// src/hooks/useOrganizationAPI.js
import { useState } from 'react';
import config from '../config';
import logger from '../utils/logger';
import { fetchOrganizationsWithInstallations } from '../utils/graphqlClient';

/**
 * Custom hook for organization CRUD operations and authorization
 * Manages API calls with loading states and result modals
 */
const useOrganizationAPI = (getAccessToken, onOrganizationsChanged, userProfile = null) => {
  const [creationModal, setCreationModal] = useState({
    isOpen: false,
    isLoading: false,
    result: null,
  });

  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    isLoading: false,
    result: null,
  });

  const [deletionModal, setDeletionModal] = useState({
    isOpen: false,
    isLoading: false,
    isRefreshing: false,
    result: null,
    isDeletingCurrent: false,
  });

  const [authorizationModal, setAuthorizationModal] = useState({
    isOpen: false,
    isLoading: false,
    result: null,
  });

  /**
   * Create a new organization
   */
  const createOrganization = async (name) => {
    logger.log('[useOrganizationAPI] Creating organization:', name);
    setCreationModal({ isOpen: true, isLoading: true, result: null });

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${config.servicesApiEndpoint}/create-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify({ name, accessToken }),
      });

      const result = await response.json();
      logger.log('[useOrganizationAPI] Create result:', result);

      if (result.success) {
        // Refresh organizations list
        await onOrganizationsChanged();

        setCreationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: 'success',
            message: `Organization "${result.organization.name}" created successfully!`,
            organization: result.organization,
          },
        });

        return { success: true, organization: result.organization };
      } else {
        setCreationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: 'error',
            message: result.message || 'Failed to create organization',
          },
        });
        return { success: false, error: result.message };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Create error:', error);
      setCreationModal({
        isOpen: true,
        isLoading: false,
        result: {
          type: 'error',
          message: error.message || 'An error occurred while creating organization',
        },
      });
      return { success: false, error: error.message };
    }
  };

  /**
   * Update organization name and settings
   */
  const updateOrganization = async (orgId, name, settings) => {
    logger.log('[useOrganizationAPI] Updating organization:', orgId);

    try {
      const accessToken = await getAccessToken();

      // Update organization name
      const response = await fetch(`${config.servicesApiEndpoint}/update-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify({ orgId, name, accessToken }),
      });

      const result = await response.json();

      if (result.success) {
        // Show loading modal for settings update
        setUpdateModal({ isOpen: true, isLoading: true, result: null });

        // Update organization settings
        try {
          const settingsResponse = await fetch(
            `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(orgId)}/settings`,
            {
              method: 'PUT',
              headers: {
                'x-api-key': config.apiKey,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ settings }),
            }
          );

          if (!settingsResponse.ok) {
            logger.error('[useOrganizationAPI] Settings update failed');
            setUpdateModal({
              isOpen: true,
              isLoading: false,
              result: {
                type: 'warning',
                message: 'Organization name updated, but settings update failed',
              },
            });
          } else {
            setUpdateModal({
              isOpen: true,
              isLoading: false,
              result: {
                type: 'success',
                message: `Organization "${result.organization.name}" updated successfully!`,
              },
            });
          }
        } catch (settingsError) {
          logger.error('[useOrganizationAPI] Settings update error:', settingsError);
          setUpdateModal({
            isOpen: true,
            isLoading: false,
            result: {
              type: 'warning',
              message: 'Organization name updated, but settings update failed',
            },
          });
        }

        // Refresh organizations list
        await onOrganizationsChanged();

        return { success: true, organization: result.organization };
      } else {
        logger.error('[useOrganizationAPI] Update failed:', result.message);
        setUpdateModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: 'error',
            message: result.message || 'Failed to update organization',
          },
        });
        return { success: false, error: result.message };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Update error:', error);
      setUpdateModal({
        isOpen: true,
        isLoading: false,
        result: {
          type: 'error',
          message: error.message || 'An error occurred while updating organization',
        },
      });
      return { success: false, error: error.message };
    }
  };

  /**
   * Delete an organization
   */
  const deleteOrganization = async (orgId, organizationName, currentOrganizationId) => {
    const isDeletingCurrent = orgId === currentOrganizationId;
    logger.log('[useOrganizationAPI] Deleting organization:', orgId);

    setDeletionModal({
      isOpen: true,
      isLoading: true,
      isRefreshing: false,
      result: null,
      isDeletingCurrent,
      organizationName,
    });

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${config.servicesApiEndpoint}/delete-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify({ orgId, accessToken }),
      });

      const result = await response.json();

      if (result.success) {
        // Show refreshing state
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: true,
          result: null,
          isDeletingCurrent,
          organizationName,
        });

        // Refresh organizations list
        await onOrganizationsChanged();

        // Show success
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: false,
          result: {
            type: 'success',
            message: `Organization "${organizationName}" deleted successfully!`,
            isDeletingCurrent,
          },
          isDeletingCurrent,
          organizationName,
        });

        return { success: true, isDeletingCurrent };
      } else {
        logger.error('[useOrganizationAPI] Delete failed:', result.message);
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: false,
          result: {
            type: 'error',
            message: result.message || 'Failed to delete organization',
          },
          isDeletingCurrent: false,
          organizationName,
        });
        return { success: false, error: result.message };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Delete error:', error);
      setDeletionModal({
        isOpen: true,
        isLoading: false,
        isRefreshing: false,
        result: {
          type: 'error',
          message: error.message || 'An error occurred',
        },
        isDeletingCurrent: false,
        organizationName,
      });
      return { success: false, error: error.message };
    }
  };

  /**
   * Authorize organization with status polling
   */
  const authorizeOrganization = async (orgId) => {
    logger.log('[useOrganizationAPI] Authorizing organization:', orgId);
    setAuthorizationModal({ isOpen: true, isLoading: true, result: null });

    try {
      const accessToken = await getAccessToken();
      const url = `${config.authorizationApiEndpoint}/grant-romanworks-integration`;
      const requestBody = { orgId, accessToken };
      
      logger.log('[useOrganizationAPI] Authorize request:', {
        url,
        method: 'POST',
        hasApiKey: !!config.apiKey,
        bodyKeys: Object.keys(requestBody)
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify(requestBody),
      });
      
      logger.log('[useOrganizationAPI] Authorize response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Handle non-200 responses with user-friendly messages
      if (!response.ok) {
        let errorMessage = 'Failed to authorize organization';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse the error response, provide status-specific messages
          if (response.status === 403) {
            errorMessage = 'You do not have permission to authorize this organization. Only Full Admins can perform this action.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (response.status === 400) {
            errorMessage = 'Invalid request. Please try again or contact support.';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again in a few moments.';
          }
        }
        throw new Error(errorMessage);
      }

      let result;
      try {
        const responseText = await response.text();
        logger.log('[useOrganizationAPI] Authorize raw response:', responseText);
        result = JSON.parse(responseText);
        logger.log('[useOrganizationAPI] Authorize parsed result:', result);
      } catch (parseError) {
        logger.error('[useOrganizationAPI] Failed to parse authorize response:', parseError);
        throw new Error('Received invalid response from server. Please try again.');
      }

      if (result.success) {
        setAuthorizationModal({
          isOpen: true,
          isLoading: true,
          result: { type: 'polling', message: 'Verifying authorization status...' },
        });

        // Poll for status change
        const statusUpdated = await pollAuthorizationStatus(orgId, 'AUTHORIZED');

        if (statusUpdated) {
          setAuthorizationModal({
            isOpen: true,
            isLoading: false,
            result: {
              type: 'success',
              message: 'Organization authorized successfully',
            },
          });
          return { success: true };
        } else {
          setAuthorizationModal({
            isOpen: true,
            isLoading: false,
            result: {
              type: 'warning',
              message:
                'Authorization initiated successfully, but status verification timed out. The authorization may still be in progress.',
            },
          });
          return { success: true, warning: 'Status verification timeout' };
        }
      } else {
        logger.error('[useOrganizationAPI] Authorize failed:', result.message);
        setAuthorizationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: 'error',
            message: result.message || 'Failed to authorize organization',
          },
        });
        return { success: false, error: result.message };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Authorize error:', error);
      
      // Provide user-friendly error messages
      let userMessage = error.message;
      if (error.message === 'Failed to fetch') {
        userMessage = 'Unable to connect to the authorization service. Please check your network connection and try again.';
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userMessage = 'Network error occurred. Please check your connection and try again.';
      } else if (!error.message) {
        userMessage = 'An unexpected error occurred while authorizing the organization. Please try again.';
      }
      
      setAuthorizationModal({
        isOpen: true,
        isLoading: false,
        result: {
          type: 'error',
          message: userMessage,
        },
      });
      return { success: false, error: userMessage };
    }
  };

  /**
   * Unauthorize organization with status polling
   */
  const unauthorizeOrganization = async (orgId) => {
    logger.log('[useOrganizationAPI] Revoking authorization for organization:', orgId);
    setAuthorizationModal({ isOpen: true, isLoading: true, result: null });

    try {
      const accessToken = await getAccessToken();
      const url = `${config.authorizationApiEndpoint}/revoke-romanworks-integration`;
      const requestBody = { 
        orgId, 
        accessToken,
        userRoles: userProfile?.roles || []
      };
      
      logger.log('[useOrganizationAPI] Unauthorize request:', {
        url,
        method: 'POST',
        hasApiKey: !!config.apiKey,
        bodyKeys: Object.keys(requestBody),
        userRolesCount: requestBody.userRoles.length
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify(requestBody),
      });
      
      logger.log('[useOrganizationAPI] Unauthorize response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Handle non-200 responses with user-friendly messages
      if (!response.ok) {
        let errorMessage = 'Failed to revoke authorization';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse the error response, provide status-specific messages
          if (response.status === 403) {
            errorMessage = 'You do not have permission to revoke authorization for this organization. Only Full Admins can perform this action.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (response.status === 400) {
            errorMessage = 'Invalid request. Please try again or contact support.';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again in a few moments.';
          }
        }
        throw new Error(errorMessage);
      }

      let result;
      try {
        const responseText = await response.text();
        logger.log('[useOrganizationAPI] Unauthorize raw response:', responseText);
        result = JSON.parse(responseText);
        logger.log('[useOrganizationAPI] Unauthorize parsed result:', result);
      } catch (parseError) {
        logger.error('[useOrganizationAPI] Failed to parse unauthorize response:', parseError);
        throw new Error('Received invalid response from server. Please try again.');
      }

      if (result.success) {
        setAuthorizationModal({
          isOpen: true,
          isLoading: true,
          result: { type: 'polling', message: 'Verifying revocation status...' },
        });

        // Poll for status change
        const statusUpdated = await pollAuthorizationStatus(orgId, 'NOT_AUTHORIZED');

        if (statusUpdated) {
          setAuthorizationModal({
            isOpen: true,
            isLoading: false,
            result: {
              type: 'success',
              message: 'Organization authorization revoked successfully',
            },
          });
          return { success: true };
        } else {
          setAuthorizationModal({
            isOpen: true,
            isLoading: false,
            result: {
              type: 'warning',
              message:
                'Revocation initiated successfully, but status verification timed out. The revocation may still be in progress.',
            },
          });
          return { success: true, warning: 'Status verification timeout' };
        }
      } else {
        logger.error('[useOrganizationAPI] Unauthorize failed:', result.message);
        setAuthorizationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: 'error',
            message: result.message || 'Failed to revoke authorization',
          },
        });
        return { success: false, error: result.message };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Unauthorize error:', error);
      
      // Provide user-friendly error messages
      let userMessage = error.message;
      if (error.message === 'Failed to fetch') {
        userMessage = 'Unable to connect to the authorization service. Please check your network connection and try again.';
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userMessage = 'Network error occurred. Please check your connection and try again.';
      } else if (!error.message) {
        userMessage = 'An unexpected error occurred while revoking authorization. Please try again.';
      }
      
      setAuthorizationModal({
        isOpen: true,
        isLoading: false,
        result: {
          type: 'error',
          message: userMessage,
        },
      });
      return { success: false, error: userMessage };
    }
  };

  /**
   * Poll for authorization status change
   */
  const pollAuthorizationStatus = async (orgId, expectedStatus) => {
    let attempts = 0;
    const maxAttempts = 10; // 5 seconds max (10 * 500ms)
    const pollInterval = 500;

    while (attempts < maxAttempts) {
      attempts++;
      logger.log(`[useOrganizationAPI] Polling attempt ${attempts}/${maxAttempts}`);

      // Refresh organizations
      if (onOrganizationsChanged) {
        await onOrganizationsChanged();
      }

      // Check current status
      const updatedOrgs = await fetchOrganizationsWithInstallations(getAccessToken);
      const updatedOrg = updatedOrgs.find((org) => org.orgId === orgId);

      if (updatedOrg?.romanworksAuthzStatus === expectedStatus) {
        logger.log('[useOrganizationAPI] Status confirmed as', expectedStatus);
        return true;
      }

      // Wait before next attempt
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    logger.log('[useOrganizationAPI] Max polling attempts reached');
    return false;
  };

  /**
   * Fetch organization settings
   */
  const fetchOrganizationSettings = async (orgId) => {
    try {
      const response = await fetch(
        `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(orgId)}/settings`,
        {
          method: 'GET',
          headers: {
            'x-api-key': config.apiKey,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          settings: {
            autoProvisionCredentials: data.settings.autoProvisionCredentials ?? false,
            receiveCredentialNotifications: data.settings.receiveCredentialNotifications ?? false,
            enableDevStreamsForAllRoles: data.settings.enableDevStreamsForAllRoles ?? false,
          },
        };
      } else {
        return { success: false };
      }
    } catch (error) {
      logger.error('[useOrganizationAPI] Error fetching settings:', error);
      return { success: false };
    }
  };

  return {
    // API methods
    createOrganization,
    updateOrganization,
    deleteOrganization,
    authorizeOrganization,
    unauthorizeOrganization,
    fetchOrganizationSettings,

    // Modal states
    creationModal,
    updateModal,
    deletionModal,
    authorizationModal,

    // Modal setters
    setCreationModal,
    setUpdateModal,
    setDeletionModal,
    setAuthorizationModal,
  };
};

export default useOrganizationAPI;
